// pages/stock/StockDailySnapshots.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Typography, Box, Grid, Chip, Stack,
    IconButton, Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { stockAPI } from '../../services/api';

const StockDailySnapshots = ({ open, onClose, stockItem, branchId, branchName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [snapshotData, setSnapshotData] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    });
    const [endDate, setEndDate] = useState(new Date());

    const formatDate = (date) => {
        if (!date) return '';
        return date.toISOString().split('T')[0];
    };

    const fetchSnapshots = useCallback(async () => {
        if (!stockItem?._id || !branchId) return;

        setLoading(true);
        try {
            const response = await stockAPI.getBranchDailySnapshots(
                stockItem._id,
                branchId,
                {
                    startDate: formatDate(startDate),
                    endDate: formatDate(endDate)
                }
            );
            setSnapshotData(response.data?.data || response.data);
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                t('stock.snapshotFetchError', 'Failed to fetch daily snapshots')
            );
        } finally {
            setLoading(false);
        }
    }, [stockItem, branchId, startDate, endDate, t]);

    useEffect(() => {
        if (open) {
            fetchSnapshots();
        }
    }, [open, fetchSnapshots]);

    const handlePreviousWeek = () => {
        const newStart = new Date(startDate);
        const newEnd = new Date(endDate);
        newStart.setDate(newStart.getDate() - 7);
        newEnd.setDate(newEnd.getDate() - 7);
        setStartDate(newStart);
        setEndDate(newEnd);
    };

    const handleNextWeek = () => {
        const newStart = new Date(startDate);
        const newEnd = new Date(endDate);
        newStart.setDate(newStart.getDate() + 7);
        newEnd.setDate(newEnd.getDate() + 7);
        setStartDate(newStart);
        setEndDate(newEnd);
    };

    const handleToday = () => {
        const d = new Date();
        const s = new Date();
        s.setDate(s.getDate() - 30);
        setStartDate(s);
        setEndDate(d);
    };

    const snapshots = snapshotData?.snapshots || [];

    // Calculate totals
    const totals = snapshots.reduce((acc, s) => ({
        added: acc.added + (s.added || 0),
        used: acc.used + (s.used || 0),
        transferred: acc.transferred + (s.transferred || 0),
    }), { added: 0, used: 0, transferred: 0 });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">
                            {t('stock.dailySnapshots', 'Daily Stock Snapshots')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {stockItem?.name} — {branchName || branchId}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Summary Cards */}
                {snapshotData && (
                    <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                        <Chip
                            label={`${t('stock.currentQuantity', 'Current Qty')}: ${snapshotData.currentQuantity ?? 0}`}
                            color="primary"
                            variant="outlined"
                        />
                        <Chip
                            label={`${t('stock.currentWac', 'Current WAC')}: ${(snapshotData.currentWac ?? 0).toFixed(2)}`}
                            color="info"
                            variant="outlined"
                        />
                        {snapshots.length > 0 && (
                            <>
                                <Chip
                                    label={`${t('stock.totalAdded', 'Total Added')}: +${totals.added}`}
                                    color="success"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`${t('stock.totalUsed', 'Total Used')}: -${totals.used}`}
                                    color="error"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`${t('stock.totalTransferred', 'Total Transferred')}: -${totals.transferred}`}
                                    color="warning"
                                    variant="outlined"

                                />
                            </>
                        )}
                    </Stack>
                )}

                {/* Date Range Filters */}
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('common.dateFrom', 'From')}
                                    value={startDate}
                                    onChange={(date) => date && setStartDate(date)}
                                    slotProps={{
                                        textField: { fullWidth: true, size: 'small' }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('common.dateTo', 'To')}
                                    value={endDate}
                                    onChange={(date) => date && setEndDate(date)}
                                    slotProps={{
                                        textField: { fullWidth: true, size: 'small' }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Stack direction="row" spacing={1}>
                                <Tooltip title={t('common.previousWeek', 'Previous Week')}>
                                    <IconButton onClick={handlePreviousWeek} size="small">
                                        <ArrowBackIcon />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CalendarIcon />}
                                    onClick={handleToday}
                                >
                                    {t('common.last30Days', 'Last 30 Days')}
                                </Button>
                                <Tooltip title={t('common.nextWeek', 'Next Week')}>
                                    <IconButton onClick={handleNextWeek} size="small">
                                        <ArrowForwardIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Snapshots Table */}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {t('stock.date', 'Date')}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {t('stock.openingQuantity', 'Opening')}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                    {t('stock.added', 'Added')}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                    {t('stock.used', 'Used')}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                    {t('stock.movedOut', 'Moved Out')}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                    {t('stock.found', 'Found')}
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                    {t('stock.closingQuantity', 'Closing')}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    {t('stock.wac', 'WAC')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : snapshots.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {t('stock.noSnapshots', 'No daily snapshots found for this date range')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {snapshots.map((snapshot, index) => {
                                        // Support both old format (outgoing) and new format (used + transferred)
                                        const used = snapshot.used ?? snapshot.outgoing ?? 0;
                                        const transferred = snapshot.transferred ?? 0;
                                        const totalOutgoing = used + transferred;
                                        const wac = snapshot.wacAtClose ?? 0;

                                        const hasDiscrepancy = snapshot.found !== null &&
                                            snapshot.found !== undefined;

                                        // Net change for color coding
                                        const netChange = (snapshot.added || 0) - totalOutgoing;

                                        return (
                                            <TableRow
                                                key={snapshot._id || index}
                                                hover
                                                sx={{
                                                    // backgroundColor: hasDiscrepancy
                                                    // ? 'rgba(255, 152, 0, 0.08)'
                                                    // : index % 2 ? 'action.hover' : 'background.paper'
                                                }}
                                            >
                                                {/* Date */}
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {snapshot.date}
                                                    </Typography>
                                                </TableCell>

                                                {/* Opening Quantity */}
                                                <TableCell align="center">
                                                    <Typography variant="body2">
                                                        {snapshot.openingQuantity}
                                                    </Typography>
                                                </TableCell>

                                                {/* Added */}
                                                <TableCell align="center">
                                                    {(snapshot.added || 0) > 0 ? (
                                                        <Chip
                                                            label={`+${snapshot.added}`}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            0
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Used (Appointments) */}
                                                <TableCell align="center">
                                                    {used > 0 ? (
                                                        <Chip
                                                            label={`-${used}`}
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            0
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Moved Out (Transferred to other branches) */}
                                                <TableCell align="center">
                                                    {transferred > 0 ? (
                                                        <Chip
                                                            label={`-${transferred}`}
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            0
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Found (Physical Count) */}
                                                <TableCell align="center">
                                                    {snapshot.found !== null && snapshot.found !== undefined ? (
                                                        <Chip
                                                            label={snapshot.found}
                                                            size="small"
                                                            color={hasDiscrepancy ? 'warning' : 'info'}
                                                            variant={hasDiscrepancy ? 'filled' : 'outlined'}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            —
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Closing Quantity */}
                                                <TableCell align="center">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color={
                                                            netChange > 0 ? 'success.main'
                                                                : netChange < 0 ? 'error.main'
                                                                    : 'text.primary'
                                                        }
                                                    >
                                                        {snapshot.closingQuantity}
                                                    </Typography>
                                                </TableCell>

                                                {/* WAC */}
                                                <TableCell align="right">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {wac > 0 ? wac.toFixed(2) : '—'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {/* Totals Row */}
                                    {snapshots.length > 1 && (
                                        <TableRow >
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {t('common.total', 'Total')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold">
                                                    {snapshots[0]?.openingQuantity ?? '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                                    +{totals.added}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="error.main">
                                                    -{totals.used}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="warning.main">
                                                    -{totals.transferred}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">—</TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold">
                                                    {snapshots[snapshots.length - 1]?.closingQuantity ?? '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight="bold">
                                                    {(snapshotData?.currentWac ?? 0).toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Legend */}
                <Box mt={2}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" color="success" variant="outlined" label="+N" />
                            <Typography variant="caption" color="text.secondary">
                                {t('stock.legendAdded', 'Added (received from transfer)')}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" color="error" variant="outlined" label="-N" />Found
                            <Typography variant="caption" color="text.secondary">
                                {t('stock.legendUsed', 'Used (consumed in appointments)')}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" color="warning" variant="outlined" label="-N" />
                            <Typography variant="caption" color="text.secondary">
                                {t('stock.legendTransferred', 'Moved Out (transferred to other branch)')}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" color="info" variant="outlined" label="N" />
                            <Typography variant="caption" color="text.secondary">
                                {t('stock.legendFound', 'Found (physical count)')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default StockDailySnapshots;